/**
 * Per-device store of `FileSystemFileHandle`s for Local SQLite
 * connections.
 *
 * The saved-connection row on the server carries only the file's
 * content hash + size. The actual handle that lets us open the file
 * lives in IndexedDB on each device that has attached it. This module
 * is the device-local sidecar.
 *
 * **States** (T6 of the plan; outside-voice finding #1 added
 * `handle-revoked`):
 *
 * - `attached`: handle present in IndexedDB, permission granted, hash
 *   and size + mtime match the server's record. Worker can open the
 *   file immediately.
 * - `needs-permission`: handle present, but `queryPermission()` returned
 *   `'prompt'`. The user gesture (a click on the card) drives
 *   `requestPermission()`.
 * - `needs-reattach-this-device`: nothing in IndexedDB for this
 *   `saved_connection_id` (first time on this device, or storage was
 *   cleared).
 * - `handle-revoked`: handle present but `queryPermission()` returned
 *   `'denied'`, or the underlying file is no longer reachable. UX:
 *   "Re-attach from picker."
 *
 * **Re-hash skip:** when a handle is re-fetched on page reload, we
 * compare `file.size` and `file.lastModified` against the IndexedDB
 * record. If both match, we trust the previously-computed hash and
 * skip the streaming SHA-256 (which would take many seconds on a
 * multi-GB file). This is the same heuristic rsync / make use; an
 * external `mtime`-preserving write to a same-size file would slip
 * past, but the WAL detection at attach + per-import still fires.
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

const DB_NAME = "mycorr.sqlite-handles";
const DB_VERSION = 1;
const STORE = "handles";

/** TS's lib.dom.d.ts lags the FSA spec — `queryPermission` /
 *  `requestPermission` are in every Chromium engine but not in the
 *  default DOM types. Augment locally rather than enabling a global
 *  experimental flag. */
interface FileSystemHandleWithPermissions {
  queryPermission(opts: {
    mode: "read" | "readwrite";
  }): Promise<PermissionState>;
  requestPermission(opts: {
    mode: "read" | "readwrite";
  }): Promise<PermissionState>;
}

/** Persisted handle record per `saved_connection_id`. */
export interface SqliteHandleRecord {
  /** Server-side saved connection ID. Primary key. */
  savedConnectionId: string;
  /** The browser-issued FSA handle. Structured-cloneable, persists
   *  across reloads via IndexedDB. */
  handle: FileSystemFileHandle;
  /** SHA-256 hex of the file's bytes when the connection was created
   *  (or last accepted on hash-mismatch prompt). */
  sha256Hash: string;
  /** File size in bytes at last successful attach. Cache key for the
   *  re-hash skip. */
  fileSize: number;
  /** `File.lastModified` (unix milliseconds) at last successful
   *  attach. Cache key for the re-hash skip. */
  lastModified: number;
  /** Unix milliseconds; when this device last successfully attached
   *  the handle. Informational; drives "Attached on this Mac N days
   *  ago" UI copy in T13; also used as the LRU sort key when the OPFS
   *  pool hits its quota. */
  lastAttachedAt: number;
  /** OPFS filename inside the SAH pool, or `null` when no cached copy
   *  exists (record has never been opened on this device, or the cache
   *  was evicted under quota pressure / cleared manually).
   *
   *  When non-null, [[registry.openConnection]] can open the DB
   *  directly without re-reading the source file. When null, the
   *  registry calls [[attachToOpfs]] to stream-copy the source into
   *  OPFS first.
   *
   *  Field is optional in the TS type to tolerate dev IDB rows from
   *  earlier v1 commits that pre-date this field; readers should
   *  default `undefined` to `null`. */
  opfsName?: string | null;
  /** `handle.name` captured at attach time — the filename the OS
   *  reported when the user picked the file. Surfaces in the
   *  connection overlay's file-details grid, distinct from the
   *  user-editable `connection.display_name`. The browser does not
   *  expose the full OS path; this is the most-granular identifier we
   *  can render. Optional for backwards compatibility with records
   *  written before the field was added — readers should default
   *  `undefined` to `null`. */
  fileName?: string | null;
}

interface HandlesSchema extends DBSchema {
  [STORE]: {
    key: string;
    value: SqliteHandleRecord;
  };
}

let dbPromise: Promise<IDBPDatabase<HandlesSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<HandlesSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<HandlesSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "savedConnectionId" });
        }
      },
    });
  }
  return dbPromise;
}

/** Persist a handle record, overwriting any existing entry for the
 *  same `saved_connection_id`. Called from the attach flow once the
 *  server has confirmed the saved-connection row (so the
 *  `savedConnectionId` we record is stable). */
export async function putSqliteHandle(
  record: Omit<SqliteHandleRecord, "lastAttachedAt"> & {
    lastAttachedAt?: number;
  },
): Promise<void> {
  const db = await getDb();
  await db.put(STORE, {
    ...record,
    lastAttachedAt: record.lastAttachedAt ?? Date.now(),
  });
}

/** Fetch the handle record for a given `saved_connection_id`, or
 *  `undefined` if this device has never attached it. */
export async function getSqliteHandle(
  savedConnectionId: string,
): Promise<SqliteHandleRecord | undefined> {
  const db = await getDb();
  return db.get(STORE, savedConnectionId);
}

/** Drop the handle record (e.g. when the user deletes the connection
 *  server-side, or rejects a hash-mismatch prompt). Idempotent. */
export async function deleteSqliteHandle(
  savedConnectionId: string,
): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, savedConnectionId);
}

/** Enumerate every handle record on this device. Used by the OPFS LRU
 *  eviction path to find the least-recently-attached record with a
 *  cached copy. The natural ordering of `db.getAll` is by primary key
 *  (savedConnectionId); the caller is responsible for sorting by
 *  `lastAttachedAt`. */
export async function listAllSqliteHandles(): Promise<SqliteHandleRecord[]> {
  const db = await getDb();
  return db.getAll(STORE);
}

/** OPFS LRU eviction candidates, oldest first. Best-effort: returns
 *  `[]` on IDB errors so the worker can still refuse with a quota
 *  error if storage is genuinely tight. */
export async function collectEvictionCandidates(): Promise<string[]> {
  try {
    const all = await listAllSqliteHandles();
    return all
      .filter((r): r is typeof r & { opfsName: string } => !!r.opfsName)
      .sort((a, b) => a.lastAttachedAt - b.lastAttachedAt)
      .map((r) => r.opfsName);
  } catch {
    return [];
  }
}

/** Patch `opfsName` on an existing record. Used by the OPFS attach
 *  path (sets to a non-null name after a successful copy) and the LRU
 *  eviction path (sets to null after removing the cached copy). No-op
 *  if the record doesn't exist — eviction races with explicit deletes
 *  shouldn't crash. */
export async function setOpfsNameForHandle(
  savedConnectionId: string,
  opfsName: string | null,
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE, "readwrite");
  const record = await tx.store.get(savedConnectionId);
  if (record) {
    await tx.store.put({
      ...record,
      opfsName,
      lastAttachedAt: opfsName !== null ? Date.now() : record.lastAttachedAt,
    });
  }
  await tx.done;
}

/** Sentinel state for the connector card / drill-down. The card's
 *  rendering branches on this; the worker only opens the file when
 *  state is `attached`. */
export type SqliteHandleState =
  | { kind: "attached"; record: SqliteHandleRecord }
  | { kind: "needs-permission"; record: SqliteHandleRecord }
  | { kind: "needs-reattach-this-device" }
  | { kind: "handle-revoked"; record: SqliteHandleRecord };

/**
 * Ensure the handle currently has read permission, re-requesting it if it has
 * lapsed to `'prompt'`. MUST be called within a user activation (e.g. a click
 * handler) so the browser's permission prompt is allowed to show — the worker
 * cannot prompt, so a lapsed permission has to be re-granted on the main thread
 * before any worker file read. Returns the resulting `PermissionState`; a
 * thrown query/request (revoked handle) collapses to `'denied'`.
 */
export async function ensureSqliteReadPermission(
  handle: FileSystemFileHandle,
): Promise<PermissionState> {
  const h = handle as unknown as FileSystemHandleWithPermissions;
  try {
    if ((await h.queryPermission({ mode: "read" })) === "granted") {
      return "granted";
    }
    return await h.requestPermission({ mode: "read" });
  } catch {
    return "denied";
  }
}

/**
 * Pure state derivation given an already-fetched record. Splits the
 * IDB lookup from the permission probe so callers (and tests) can
 * exercise the two halves independently.
 *
 * Per the FSA spec, `queryPermission` may also throw if the handle
 * itself has been invalidated (e.g. the file was deleted out from
 * under us). We treat that as `handle-revoked` so the UI surfaces a
 * single "re-attach" path for every recoverable failure mode.
 */
export async function deriveSqliteHandleStateFromRecord(
  record: SqliteHandleRecord | undefined,
): Promise<SqliteHandleState> {
  if (!record) {
    return { kind: "needs-reattach-this-device" };
  }

  let permission: PermissionState;
  try {
    const handle = record.handle as unknown as FileSystemHandleWithPermissions;
    permission = await handle.queryPermission({ mode: "read" });
  } catch {
    return { kind: "handle-revoked", record };
  }

  switch (permission) {
    case "granted":
      return { kind: "attached", record };
    case "prompt":
      return { kind: "needs-permission", record };
    case "denied":
      return { kind: "handle-revoked", record };
  }
}

/**
 * Convenience: read the IndexedDB record for `savedConnectionId` and
 * derive its current state. Equivalent to
 * `getSqliteHandle(id).then(deriveSqliteHandleStateFromRecord)`.
 *
 * Does NOT re-hash the file — that's a separate, caller-driven step
 * gated on `file.size + file.lastModified` matching the stored cache
 * key (see `shouldSkipRehash`).
 */
export async function deriveSqliteHandleState(
  savedConnectionId: string,
): Promise<SqliteHandleState> {
  const record = await getSqliteHandle(savedConnectionId);
  return deriveSqliteHandleStateFromRecord(record);
}

/** Cheap predicate: should we skip the re-hash on re-attach? Returns
 *  true iff `file.size` AND `file.lastModified` both match the stored
 *  record. Caller is responsible for actually running the hash when
 *  this returns false. */
export function shouldSkipRehash(
  record: SqliteHandleRecord,
  freshFile: { size: number; lastModified: number },
): boolean {
  return (
    record.fileSize === freshFile.size &&
    record.lastModified === freshFile.lastModified
  );
}

/** Reset module state. Test-only — closes any open DB connection so
 *  the next call to `getDb()` re-opens. Production code never needs
 *  this. */
export function __resetForTests(): void {
  if (dbPromise) {
    dbPromise.then((db) => db.close()).catch(() => {});
    dbPromise = null;
  }
}
